import { useRef } from 'react'
import { gql } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react'
import {
  ensureAiChatSession,
  getGraphqlErrorMessage,
} from './ai-chat-live-session'
import {
  cancelAiChatInputRequest,
  streamAiChatAnswers,
  streamAiChatMessage,
} from './ai-chat-stream-client'
import { applyAiChatStreamEvent } from './ai-chat-stream-state'
import type {
  AiChatDecisionAnswer,
  AiChatInputRequestData,
} from '@amitable/shared/ai-chat-decisions'
import type { AiChatNavigationContext } from '@amitable/shared/ai-chat-navigation'
import type { AiChatStreamEvent } from './ai-chat-stream-client'
import type { Dispatch, SetStateAction } from 'react'
import type { LiveAiChatMessage, LiveAiChatScope } from './ai-chat-live-content'
import type { useStartDocumentCollectionAiChatMutation } from '@/graphql/mutations/startDocumentCollectionAiChatMutation.gql.generated'
import type { useStartWorkspaceAiChatMutation } from '@/graphql/mutations/startWorkspaceAiChatMutation.gql.generated'
import type { DocumentsStoreApi } from '@/integrations/zustand-store/features/documents/documents-store'
import { useOptionalDocumentsStoreApi } from '@/integrations/zustand-store/root-provider'
import { McpOperationKind } from '@/graphql/schema'
import { UNSCOPED_DOCUMENTS_LOAD_KEY } from '@/integrations/zustand-store/features/documents/documents-store-constants'

type StartWorkspaceChat = ReturnType<typeof useStartWorkspaceAiChatMutation>[0]
type StartDocumentCollectionChat = ReturnType<
  typeof useStartDocumentCollectionAiChatMutation
>[0]

export type SentMessagesBySessionId = Record<string, Array<LiveAiChatMessage>>
export type StreamAbortControllerRef = {
  current: AbortController | null
}

type UseAiChatSubmitPromptInput = {
  navigationContext?: AiChatNavigationContext
  activeSessionId?: string
  blocked: boolean
  documentCollectionId?: string
  scope: LiveAiChatScope
  sessionsRefetch: () => Promise<unknown>
  messages: Array<LiveAiChatMessage>
  messagesRefetch: (sessionId: string) => Promise<{
    data?: { aiChatMessages: Array<LiveAiChatMessage> }
  }>
  setCreatedSessionId: (sessionId: string) => void
  onUserMessageAccepted: (sessionId: string, message: string) => void
  onSessionUpdated: (sessionId: string, title: string) => void
  setResponding: (responding: boolean) => void
  setSentMessagesBySessionId: Dispatch<SetStateAction<SentMessagesBySessionId>>
  setSubmitting: (submitting: boolean) => void
  setSubmitError: (errorMessage: string | undefined) => void
  startDocumentCollectionChat: StartDocumentCollectionChat
  startWorkspaceChat: StartWorkspaceChat
  streamAbortControllerRef: StreamAbortControllerRef
  workspaceId?: string
}

type DecisionSubmission = {
  kind: 'answer' | 'cancel'
  request: AiChatInputRequestData
  answers?: Array<AiChatDecisionAnswer>
}
type Submission = {
  sessionId?: string
  message: string
  fileIds?: Array<string>
  onAccepted?: (sessionId: string) => void
  decision?: DecisionSubmission
}

export function useAiChatSubmitPrompt(input: UseAiChatSubmitPromptInput) {
  const client = useApolloClient()
  const documentsStore = useOptionalDocumentsStoreApi()
  const submittingRef = useRef(false)
  const submit = async (
    prompt: string,
    onAccepted?: Submission['onAccepted'],
    decision?: DecisionSubmission,
    fileIds?: Array<string>,
  ) => {
    const message = prompt.trim()
    if (
      (!message && !fileIds?.length) ||
      input.blocked ||
      submittingRef.current
    )
      return
    submittingRef.current = true
    input.setSubmitError(undefined)
    input.setSubmitting(true)
    input.setResponding(decision?.kind !== 'cancel')
    const submission = {
      message,
      fileIds,
      onAccepted,
      decision,
      sessionId: decision?.request.sessionId ?? input.activeSessionId,
    }
    try {
      await runSubmission(input, { client, documentsStore }, submission)
    } catch (error) {
      await handleSubmissionFailure(input, error, submission)
    } finally {
      submittingRef.current = false
      input.setSubmitting(false)
      input.setResponding(false)
      input.streamAbortControllerRef.current = null
    }
  }

  return Object.assign(submit, {
    answerInputRequest: (
      request: AiChatInputRequestData,
      answers: Array<AiChatDecisionAnswer>,
    ) =>
      submit('Answer AI decision', undefined, {
        kind: 'answer',
        request,
        answers,
      }),
    cancelInputRequest: (request: AiChatInputRequestData) =>
      submit('Cancel AI decision', undefined, { kind: 'cancel', request }),
  })
}

async function handleSubmissionFailure(
  input: UseAiChatSubmitPromptInput,
  error: unknown,
  submission: Submission,
) {
  const aborted = error instanceof DOMException && error.name === 'AbortError'
  if (!aborted) input.setSubmitError(getGraphqlErrorMessage(error))
  const sessionId = submission.sessionId
  if (sessionId) {
    const refreshed = await input
      .messagesRefetch(sessionId)
      .catch(() => undefined)
    const data = refreshed?.data
    if (data)
      input.setSentMessagesBySessionId((current) => ({
        ...current,
        [sessionId]: data.aiChatMessages,
      }))
  }
  if (submission.decision && !aborted) throw error
}

async function runSubmission(
  input: UseAiChatSubmitPromptInput,
  refreshTargets: RefreshTargets,
  submission: Submission,
) {
  const sessionId =
    submission.decision?.request.sessionId ?? (await ensureAiChatSession(input))
  submission.sessionId = sessionId
  const abortController = new AbortController()
  input.streamAbortControllerRef.current?.abort()
  input.streamAbortControllerRef.current = abortController
  input.setSentMessagesBySessionId((current) => ({
    ...current,
    [sessionId]: [
      ...new Map(
        [
          ...input.messages.filter((item) => item.sessionId === sessionId),
          ...(current[sessionId] ?? []),
        ].map((item) => [item.id, item]),
      ).values(),
    ],
  }))
  const onEvent = createStreamEventHandler({
    ...input,
    ...refreshTargets,
    sessionId,
    onAccepted: submission.onAccepted,
    streamingContentRef: { current: '' },
    streamingToolCallsRef: { current: [] as LiveAiChatMessage['toolCalls'] },
  })
  await dispatchSubmission({
    input,
    submission,
    sessionId,
    signal: abortController.signal,
    onEvent,
  })
  await input.sessionsRefetch()
}

async function dispatchSubmission({
  input,
  submission,
  sessionId,
  signal,
  onEvent,
}: {
  input: UseAiChatSubmitPromptInput
  submission: Submission
  sessionId: string
  signal: AbortSignal
  onEvent: (event: AiChatStreamEvent) => void
}) {
  const decision = submission.decision
  if (decision?.kind === 'cancel') {
    const inputRequest = await cancelAiChatInputRequest(
      sessionId,
      decision.request.id,
    )
    onEvent({ type: 'input_request_updated', inputRequest })

    return
  }
  if (decision) {
    await streamAiChatAnswers({
      sessionId,
      requestId: decision.request.id,
      answers: decision.answers ?? [],
      signal,
      onEvent,
    })

    return
  }
  await streamAiChatMessage({
    navigationContext: input.navigationContext,
    message: submission.message,
    fileIds: submission.fileIds,
    sessionId,
    signal,
    onEvent,
  })
}

type RefreshTargets = {
  client: ReturnType<typeof useApolloClient>
  documentsStore: DocumentsStoreApi | null
}

type EventHandlerInput = Pick<
  UseAiChatSubmitPromptInput,
  | 'onUserMessageAccepted'
  | 'onSessionUpdated'
  | 'setResponding'
  | 'setSentMessagesBySessionId'
> &
  RefreshTargets & {
    sessionId: string
    onAccepted?: (sessionId: string) => void
    streamingContentRef: { current: string }
    streamingToolCallsRef: { current: LiveAiChatMessage['toolCalls'] }
  }

function createStreamEventHandler({
  client,
  documentsStore,
  sessionId,
  onAccepted,
  onUserMessageAccepted,
  onSessionUpdated,
  setResponding,
  setSentMessagesBySessionId,
  streamingContentRef,
  streamingToolCallsRef,
}: EventHandlerInput) {
  return (event: AiChatStreamEvent) => {
    refreshAfterTool({ client, documentsStore }, event)
    if (event.type === 'user_message') {
      onAccepted?.(sessionId)
      onUserMessageAccepted(sessionId, event.userMessage.content)
    }
    if (event.type === 'assistant_done') setResponding(false)
    if (event.type === 'session_updated') {
      onSessionUpdated(event.session.id, event.session.title)
      client.writeFragment({
        id: `AiChatSession:${event.session.id}`,
        fragment: gql`
          fragment UpdatedAiChatTitle on AiChatSession {
            title
          }
        `,
        data: { title: event.session.title },
      })
    }
    applyAiChatStreamEvent({
      event,
      sessionId,
      setSentMessagesBySessionId,
      streamingContentRef,
      streamingToolCallsRef,
    })
  }
}

function refreshAfterTool(
  { client, documentsStore }: RefreshTargets,
  event: AiChatStreamEvent,
) {
  if (event.type !== 'tool_call' || event.toolCall.status !== 'SUCCESS') return
  if (event.toolCall.operationKind === McpOperationKind.MetadataWrite) {
    void client
      .refetchQueries({
        include: [
          'DocumentCollections',
          'DocumentCollectionsWithFields',
          'DocumentCollection',
          'DocumentCollectionWithFields',
        ],
      })
      .catch(() => undefined)
  }
  if (event.toolCall.operationKind === McpOperationKind.AutomationWrite) {
    // Only active queries are refetched, so closed automation pages stay idle.
    void client
      .refetchQueries({
        include: ['Automations', 'Automation', 'AutomationExecutions'],
      })
      .catch(() => undefined)
  }
  if (event.toolCall.operationKind === McpOperationKind.RecordsWrite) {
    reloadAffectedCollections(
      documentsStore,
      event.toolCall.documentCollectionId,
    )
  }
}

// Reload only the affected collection; without an id, reload every loaded one.
function reloadAffectedCollections(
  documentsStore: DocumentsStoreApi | null,
  affectedId: string | null | undefined,
) {
  if (!documentsStore) return
  const { load, loadStatusByCollectionId } = documentsStore.getState()
  const collectionIds = affectedId
    ? [affectedId]
    : Object.entries(loadStatusByCollectionId)
        .filter(
          ([id, status]) =>
            status === 'succeeded' && id !== UNSCOPED_DOCUMENTS_LOAD_KEY,
        )
        .map(([id]) => id)
  for (const documentCollectionId of collectionIds)
    void load({ documentCollectionId, setLoadState: false }).catch(
      () => undefined,
    )
}
